/**
 * Search Filter Chips
 *
 * Displays filters as chips with dropdown menus in the results view.
 * Uses dynamic facets loaded from the backend.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import type { SearchFilters } from '../../types/search';
import { useFacets, getFacetValues } from '../../hooks/useFacets';
import { isFacetValueDisabled } from '../../utils/cooccurrenceIndex';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SearchFilterChipsProps {
  filters: SearchFilters;
  onChange: (filters: SearchFilters) => void;
  /** Selected keywords; they count as active filters for "Clear all filters". */
  keywordCount?: number;
  /** Replaces the default "clear all" (regular filters only), e.g. to clear keywords too. */
  onClearAll?: () => void;
}

export function SearchFilterChips({ filters, onChange, keywordCount = 0, onClearAll }: SearchFilterChipsProps) {
  const { t } = useTranslation('search');
  const { facets, loading, cooccurrenceIndex } = useFacets();
  
  // Each filter category gets its own anchor element for its dropdown menu
  const [anchorEls, setAnchorEls] = useState<Record<string, HTMLElement | null>>({});

  const openMenu = useCallback((key: string, el: HTMLElement) => {
    setAnchorEls((prev) => ({ ...prev, [key]: el }));
  }, []);

  const closeMenu = useCallback((key: string) => {
    setAnchorEls((prev) => ({ ...prev, [key]: null }));
  }, []);

  const toggleValue = useCallback(
    (key: string, value: string) => {
      const current = (filters[key as keyof SearchFilters] as string[] | undefined) ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      onChange({
        ...filters,
        [key]: next.length > 0 ? next : undefined,
      });
    },
    [filters, onChange]
  );

  const clearAll = useCallback(() => {
    if (onClearAll) onClearAll();
    else onChange({});
  }, [onChange, onClearAll]);

  const activeCount =
    keywordCount +
    facets.reduce(
      (sum, field) => sum + ((filters[field.field_name as keyof SearchFilters] as string[] | undefined)?.length ?? 0),
      0
    );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="rounded" width={80} height={24} />
        <Skeleton variant="rounded" width={100} height={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      {facets.map((field) => {
        const selected = (filters[field.field_name as keyof SearchFilters] as string[] | undefined) ?? [];
        const isActive = selected.length > 0;
        const sortedValues = getFacetValues(field);
        const isAllSelected = selected.length === sortedValues.length && sortedValues.length > 0;
        const facetDisplayName = t(`facets.${field.field_name}`, { defaultValue: field.display_name });
        const chipLabel = isAllSelected
          ? `${facetDisplayName} (${t('filters.all')})`
          : isActive
            ? `${facetDisplayName} (${selected.length})`
            : facetDisplayName;

        return (
          <Box key={field.field_name} component="span">
            <Chip
              label={chipLabel}
              size="medium"
              variant={isActive ? 'filled' : 'outlined'}
              onClick={(e) => openMenu(field.field_name, e.currentTarget)}
              onDelete={isActive ? () => onChange({ ...filters, [field.field_name]: undefined }) : undefined}
              icon={<ArrowDropDownIcon fontSize="small" />}
              sx={{
                flexDirection: 'row-reverse',
                ...(isActive && {
                  bgcolor: 'secondaryContainer',
                  color: 'onSecondaryContainer',
                }),
                '& .MuiChip-icon': {
                  ml: -0.5,
                  mr: 0.5,
                },
                '& .MuiChip-deleteIcon': {
                  mr: 0,
                  ml: 0.5,
                },
              }}
            />
            <Menu
              anchorEl={anchorEls[field.field_name] ?? null}
              open={Boolean(anchorEls[field.field_name])}
              onClose={() => closeMenu(field.field_name)}
              slotProps={{ paper: { sx: { minWidth: 360, maxHeight: 600, display: 'flex', flexDirection: 'column' } } }}
            >
              {/* Header with title and close button */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                <Typography variant="subtitle2">{facetDisplayName}</Typography>
                <IconButton size="small" onClick={() => closeMenu(field.field_name)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* Scrollable options list */}
              <Box sx={{ overflow: 'auto', flex: 1 }}>
                {sortedValues.map((facetValue) => {
                  const checked = selected.includes(facetValue.value);
                  const disabled =
                    !checked &&
                    isFacetValueDisabled(
                      cooccurrenceIndex,
                      field.field_name,
                      facetValue.value,
                      filters as Record<string, readonly string[] | undefined>,
                    );
                  // Prefer i18n translation for the facet value (e.g. source, source_type),
                  // fall back to language-specific key, then backend-provided display_name.
                  const translatedValue = t(
                    `facets.values.${field.field_name}.${facetValue.value}`,
                    { defaultValue: '' }
                  );
                  const valueDisplayName = translatedValue
                    ? translatedValue
                    : field.field_name === 'language'
                      ? t(`languages.${facetValue.value}`, { defaultValue: facetValue.display_name })
                      : facetValue.display_name;
                  return (
                    <MenuItem
                      key={facetValue.value}
                      dense
                      disabled={disabled}
                      onClick={() => toggleValue(field.field_name, facetValue.value)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          disableRipple
                          tabIndex={-1}
                        />
                      </ListItemIcon>
                      <ListItemText primary={valueDisplayName} />
                    </MenuItem>
                  );
                })}
              </Box>
              
              {/* Footer with action buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, py: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    const allValues = sortedValues.map((v) => v.value);
                    onChange({ ...filters, [field.field_name]: allValues });
                  }}
                >
                  {t('filters.selectAll')}
                </Button>
              </Box>
            </Menu>
          </Box>
        );
      })}

      {activeCount > 0 && (
        <Button
          size="small"
          onClick={clearAll}
          sx={{ textTransform: 'none', ml: 0.5, height: 32, py: 0, whiteSpace: 'nowrap' }}
        >
          {t('filters.clearAll')}
        </Button>
      )}
    </Box>
  );
}
